/**
 * My Payslips — self-service, no permission required.
 * GET /payslips/mine?page&size — only ever returns payslips for periods that are
 * APPROVED or PAID (backend never shows draft/pending figures to the employee).
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { FileText, Wallet } from "lucide-react";
import { leaveApi } from "../../leave/api";
import { payrollApi, downloadBlob } from "../api";
import { useAsyncData, usePagination, getErrorMessage } from "../hooks";
import {
  Btn, Card, CardHeader, EmptyState, PageHeader, PayrollError, PayrollLoading, Pagination,
  Table, TBody, TD, TH, THead, TR, fmt,
} from "../components/PayrollUI";

const currentYear = new Date().getFullYear();

export default function PayslipsMinePage() {
  const navigate = useNavigate();
  const pagination = usePagination(0, 12);
  const [pdfBusy, setPdfBusy] = useState<number | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [p9Year, setP9Year] = useState(currentYear);
  const [p9Busy, setP9Busy] = useState<"pdf" | "xlsx" | null>(null);
  const [p9Error, setP9Error] = useState<string | null>(null);

  const me = useAsyncData(() => leaveApi.currentEmployee(), []);
  const { data, loading, error, reload } = useAsyncData(
    () => payrollApi.myPayslips(pagination.page, pagination.size),
    [pagination.page, pagination.size],
  );

  async function downloadPdf(periodId: number, label: string) {
    setPdfBusy(periodId);
    setPdfError(null);
    try {
      const blob = await payrollApi.myPayslipPdf(periodId);
      downloadBlob(blob, `payslip-${label}.pdf`);
    } catch (err) {
      setPdfError(getErrorMessage(err));
    } finally {
      setPdfBusy(null);
    }
  }

  async function downloadMyP9(format: "pdf" | "xlsx") {
    if (!me.data) return;
    setP9Busy(format);
    setP9Error(null);
    try {
      const blob = await payrollApi.employeeP9(me.data.id, p9Year, format);
      downloadBlob(blob, `p9-${me.data.employeeNumber}-${p9Year}.${format}`);
    } catch (e) {
      setP9Error(e instanceof Error ? e.message : "Could not generate P9");
    } finally {
      setP9Busy(null);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        breadcrumb="Finance"
        title="My Payslips"
      />

      <Card noPad>
        <CardHeader title="My P9 Tax Certificate" />
        <div className="flex flex-wrap items-end gap-3 p-5">
          <label className="flex flex-col gap-1 text-xs font-semibold text-slate-500">
            Year
            <input type="number" className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400" value={p9Year} onChange={(e) => setP9Year(Number(e.target.value))} />
          </label>
          <Btn icon={FileText} loading={p9Busy === "pdf"} loadingText="Generating…" disabled={!me.data} onClick={() => downloadMyP9("pdf")}>Download P9 (PDF)</Btn>
          <Btn variant="secondary" loading={p9Busy === "xlsx"} loadingText="Generating…" disabled={!me.data} onClick={() => downloadMyP9("xlsx")}>Download P9 (Excel)</Btn>
          {p9Error && <p className="text-sm text-red-600">{p9Error}</p>}
        </div>
      </Card>

      {loading && <PayrollLoading label="Loading your payslips…" />}
      {error && <PayrollError message={error} onRetry={reload} />}

      {!loading && data && (
        <>
          <Table>
            <THead>
              <TH>Period</TH><TH right>Gross Pay</TH><TH right>Deductions</TH>
              <TH right>Net Pay</TH><TH right>Actions</TH>
            </THead>
            <TBody>
              {data.content.length === 0 ? (
                <tr><td colSpan={5}><EmptyState icon={Wallet} title="No payslips yet" description="Check back once your first finance period has been approved." /></td></tr>
              ) : data.content.map((p) => (
                <TR key={p.id}>
                  <TD className="font-semibold text-slate-800">{p.periodCode ?? p.periodId}</TD>
                  <TD right>{fmt(p.grossPay)}</TD>
                  <TD right muted>{fmt(p.totalDeductions)}</TD>
                  <TD right><span className="font-semibold text-emerald-700">{fmt(p.netPay)}</span></TD>
                  <TD right>
                    <div className="flex justify-end gap-1.5">
                      <Btn size="sm" onClick={() => navigate(`/payroll/payslips/${p.id}`)}>View</Btn>
                      <Btn size="sm" variant="secondary" loading={pdfBusy === p.periodId} loadingText="…" onClick={() => downloadPdf(p.periodId, p.periodCode ?? String(p.periodId))}>PDF</Btn>
                    </div>
                  </TD>
                </TR>
              ))}
            </TBody>
          </Table>
          <Pagination page={data} onPageChange={pagination.setPage} onSizeChange={pagination.setSize} />
          {pdfError && <p className="text-sm text-red-600">{pdfError}</p>}
        </>
      )}
    </div>
  );
}
